import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import LinkIcon from '@mui/icons-material/Link';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import XIcon from '@mui/icons-material/X';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { trackEvent } from '../../utils/analytics';
import {
  buildChallengeUrl,
  canUseNativeShare,
  copyTextToClipboard,
  ShareChannel,
} from '../../utils/share';

const IconButton = ({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    onClick={onClick}
    className="flex w-9 h-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-[#475569] transition-colors hover:bg-gray-100 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-600"
  >
    {children}
  </button>
);

/**
 * The compact share row inside the end screen's stats card. It shares the same
 * message the main share button produces — only the destinations differ.
 */
export const ShareScoreIcons = ({ body }: { body: string }) => {
  const { t } = useTranslation();

  const eventParams = { content_type: 'flagle_result', mode: 'daily' };
  const textFor = (channel: ShareChannel) =>
    `${body}\n${buildChallengeUrl(channel)}`;

  const copy = async () => {
    trackEvent('share_click', { ...eventParams, method: 'clipboard' });
    if (await copyTextToClipboard(textFor('clipboard'))) {
      trackEvent('share', { ...eventParams, method: 'clipboard' });
      toast.success(t('copy'), { autoClose: 2000 });
      return;
    }
    trackEvent('share_failed', {
      ...eventParams,
      method: 'clipboard',
      error: 'clipboard_unavailable',
    });
    toast.error(t('sharing.copyFailed'), { autoClose: 3000 });
  };

  /** Just the challenge link, for people who only want to pass the game on. */
  const copyLink = async () => {
    trackEvent('share_click', { ...eventParams, method: 'link' });
    if (await copyTextToClipboard(buildChallengeUrl('link'))) {
      trackEvent('share', { ...eventParams, method: 'link' });
      toast.success(t('sharing.linkCopied'), { autoClose: 2000 });
      return;
    }
    trackEvent('share_failed', {
      ...eventParams,
      method: 'link',
      error: 'clipboard_unavailable',
    });
    toast.error(t('sharing.copyFailed'), { autoClose: 3000 });
  };

  const openIn = (channel: ShareChannel, href: string) => {
    trackEvent('share_click', { ...eventParams, method: channel });
    trackEvent('share', { ...eventParams, method: channel });
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const nativeShare = async () => {
    trackEvent('share_click', { ...eventParams, method: 'native' });
    try {
      await navigator.share({ title: 'Flagle', text: textFor('native') });
      trackEvent('share', { ...eventParams, method: 'native' });
    } catch (error) {
      // Dismissing the sheet rejects with AbortError — a cancellation, not a
      // failure, and worth separating in reporting.
      if ((error as Error)?.name === 'AbortError') {
        trackEvent('share_cancelled', { ...eventParams, method: 'native' });
        return;
      }
      await copy();
    }
  };

  return (
    <div className="flex gap-1.5 justify-center">
      <IconButton label={t('sharing.copyResult')} onClick={copy}>
        <ContentCopyIcon fontSize="small" />
      </IconButton>

      <IconButton
        label="X"
        onClick={() =>
          openIn(
            'x',
            `https://twitter.com/intent/tweet?text=${encodeURIComponent(
              textFor('x'),
            )}`,
          )
        }
      >
        <XIcon fontSize="small" />
      </IconButton>

      <IconButton
        label="WhatsApp"
        onClick={() =>
          openIn(
            'whatsapp',
            `https://wa.me/?text=${encodeURIComponent(textFor('whatsapp'))}`,
          )
        }
      >
        <WhatsAppIcon fontSize="small" />
      </IconButton>

      <IconButton label={t('sharing.copyLink')} onClick={copyLink}>
        <LinkIcon fontSize="small" />
      </IconButton>

      {/* Only offered where the device actually has a share sheet. */}
      {canUseNativeShare() && (
        <IconButton label={t('sharing.withFriends')} onClick={nativeShare}>
          <IosShareRoundedIcon fontSize="small" />
        </IconButton>
      )}
    </div>
  );
};
